import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValueEvent, useScroll } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ChevronDown, User } from "lucide-react";
import { Logo } from "../components/Logo";
import { scrollToSection } from "./Hero";
import { useLang } from "../lib/i18n";
import { useAuth } from "../auth/AuthContext";
import { SERVICES, SERVICE_ORDER } from "../pages/diensten/data";

const FlagNL = () => (
  <svg width="18" height="13" viewBox="0 0 18 13" className="rounded-[2px]" aria-hidden="true">
    <rect width="18" height="4.4" fill="#AE1C28" />
    <rect y="4.4" width="18" height="4.2" fill="#ffffff" />
    <rect y="8.6" width="18" height="4.4" fill="#21468B" />
  </svg>
);

const FlagGB = () => (
  <svg width="18" height="13" viewBox="0 0 18 13" className="rounded-[2px]" aria-hidden="true">
    <rect width="18" height="13" fill="#012169" />
    <path d="M0,0 L18,13 M18,0 L0,13" stroke="#ffffff" strokeWidth="2.6" />
    <path d="M0,0 L18,13 M18,0 L0,13" stroke="#C8102E" strokeWidth="0.9" />
    <rect x="7.4" width="3.2" height="13" fill="#ffffff" />
    <rect y="4.9" width="18" height="3.2" fill="#ffffff" />
    <rect x="8.1" width="1.8" height="13" fill="#C8102E" />
    <rect y="5.6" width="18" height="1.8" fill="#C8102E" />
  </svg>
);

export const TopBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  const { lang, setLang, t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const closeTimer = useRef(null);

  useMotionValueEvent(scrollY, "change", (v) => {
    setScrolled(v > 24);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setServicesOpen(false), 2000);
  });

  const [servicesOpen, setServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const servicesBtnRef = useRef(null);
  const [panelLeft, setPanelLeft] = useState(null);

  const toggleServices = () => {
    if (!servicesOpen && servicesBtnRef.current && dropdownRef.current) {
      const b = servicesBtnRef.current.getBoundingClientRect();
      const n = dropdownRef.current.getBoundingClientRect();
      setPanelLeft(b.left - n.left);
    }
    setServicesOpen((v) => !v);
  };

  useEffect(() => {
    if (!servicesOpen) return;
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setServicesOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setServicesOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [servicesOpen]);

  const goToService = (slug) => {
    setServicesOpen(false);
    navigate(`/diensten/${slug}`);
  };

  const NAV = [
    { label: t.navAbout, sel: "#over-ons", testid: "nav-about" },
    { label: t.navServices, sel: "#diensten", testid: "nav-services" },
    { label: t.navCases, to: "/cases", testid: "nav-cases" },
    { label: t.navTestimonial, sel: "#klanten", testid: "nav-testimonial" },
    { label: t.navBook, sel: "#gesprek", testid: "nav-book" },
    { label: t.navFaq, sel: "#faq", testid: "nav-faq" },
  ];

  const pill = scrolled
    ? "border-edge/60 text-cream hover:border-amber/70"
    : "border-white/25 text-white/85 hover:border-white/60";

  return (
    <motion.header
      initial={{ y: -160, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="top-bar-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "border-b border-edge/40 bg-white/85 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="relative mx-auto flex min-h-[88px] max-w-7xl items-center justify-between px-6 py-4 md:min-h-[100px] md:px-12 lg:px-20">
        <div>
          <a
            href={user ? "/admin" : "/login"}
            data-testid="topbar-login-button"
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${pill}`}
          >
            <User size={13} />
            <span className="hidden sm:inline">{user ? "Beheer" : "Inloggen"}</span>
          </a>
        </div>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <Logo light={!scrolled} />
        </div>
        <div>
        <button
          onClick={() => setLang(lang === "nl" ? "en" : "nl")}
          className={`flex items-center rounded-full border p-2 transition-colors duration-500 sm:hidden ${
            scrolled ? "border-edge/50 bg-white/70" : "border-white/20 bg-black/25 backdrop-blur-md"
          }`}
          data-testid="lang-toggle-switch"
          aria-label={lang === "nl" ? "Switch to English" : "Wissel naar Nederlands"}
        >
          {lang === "nl" ? <FlagGB /> : <FlagNL />}
        </button>
        <div
          className={`hidden items-center gap-1 rounded-full border p-1 transition-colors duration-500 sm:flex ${
            scrolled ? "border-edge/50 bg-white/70" : "border-white/20 bg-black/25 backdrop-blur-md"
          }`}
          data-testid="lang-toggle"
        >
          {[
            { code: "nl", Flag: FlagNL },
            { code: "en", Flag: FlagGB },
          ].map(({ code, Flag }) => (
            <button
              key={code}
              onClick={() => setLang(code)}
              data-testid={`lang-toggle-${code}`}
              aria-label={code === "nl" ? "Nederlands" : "English"}
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-all duration-300 ${
                lang === code
                  ? scrolled
                    ? "border border-[#181510]/30 bg-[#181510]/5 text-cream"
                    : "border border-white/40 bg-white/10 text-white"
                  : scrolled
                    ? "border border-transparent text-faint opacity-70 hover:opacity-100 hover:text-cream"
                    : "border border-transparent text-white/60 hover:text-white"
              }`}
            >
              <Flag />
              {code}
            </button>
          ))}
        </div>
        </div>
      </div>

      <nav
        ref={dropdownRef}
        className={`relative transition-colors duration-500 ${scrolled ? "border-t border-edge/30" : ""}`}
        data-testid="main-nav"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-start gap-2 overflow-x-auto px-6 pb-3.5 pt-3 [scrollbar-width:none] md:justify-center md:px-12 lg:px-20 [&::-webkit-scrollbar]:hidden">
          {NAV.map((n) =>
            n.testid === "nav-services" ? (
              <button
                key={n.testid}
                ref={servicesBtnRef}
                onClick={toggleServices}
                data-testid={n.testid}
                aria-expanded={servicesOpen}
                className={`flex items-center gap-1 whitespace-nowrap rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${pill} ${servicesOpen ? "!border-amber/70" : ""}`}
              >
                {n.label}
                <ChevronDown size={11} className={`transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
            ) : (
              <button
                key={n.testid}
                onClick={() => (n.to ? navigate(n.to) : scrollToSection(n.sel))}
                data-testid={n.testid}
                className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${pill}`}
              >
                {n.label}
              </button>
            )
          )}
        </div>

        <AnimatePresence>
          {servicesOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-x-4 top-full z-50 mt-2 flex flex-col gap-1.5 rounded-[16px] border border-edge/60 bg-white p-2.5 shadow-xl shadow-black/10 md:inset-x-auto md:w-[362px]"
              style={panelLeft !== null && typeof window !== "undefined" && window.innerWidth >= 768 ? { left: panelLeft } : undefined}
              data-testid="services-dropdown"
            >
              {SERVICE_ORDER.map((slug, i) => (
                <button
                  key={slug}
                  onClick={() => goToService(slug)}
                  className="group flex w-full items-center justify-center rounded-full border border-edge/70 bg-white px-4 py-2.5 text-center transition-colors duration-300 hover:border-amber hover:bg-amber"
                  data-testid={`services-dropdown-item-${i}`}
                >
                  <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-cream transition-colors duration-300 group-hover:text-[#181510]">
                    {SERVICES[slug][lang].title}
                  </span>
                </button>
              ))}
              <button
                onClick={() => { setServicesOpen(false); navigate("/diensten"); }}
                className="mt-1 flex w-full items-center justify-center rounded-full bg-[#181510] px-4 py-2.5 text-center transition-colors duration-300 hover:bg-black"
                data-testid="services-dropdown-all"
              >
                <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-amber">
                  {lang === "nl" ? "Alle diensten" : "All services"}
                </span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};
